import { useState, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Clock } from 'lucide-react';

interface DndCalendarWrapperProps {
  children: ReactNode;
  onDropSlot: (slotData: any, targetDate: string) => void;
  formatTime12Hour: (time: string) => string;
  getUserColor: (user?: any, fallback?: string) => string;
}

export function DndCalendarWrapper({ children, onDropSlot, formatTime12Hour, getUserColor }: DndCalendarWrapperProps) {
  const [activeItem, setActiveItem] = useState<any>(null);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 250, tolerance: 5 },
  });

  const sensors = useSensors(pointerSensor, touchSensor);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveItem(event.active.data.current);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveItem(null);

    if (!event.over) return;

    const dropData = event.over.data.current as { dateString?: string } | undefined;
    const dragData = event.active.data.current;

    if (dropData?.dateString && dragData) {
      // Don't drop on the same day
      if (dragData.date !== dropData.dateString) {
        onDropSlot(dragData, dropData.dateString);
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {children}

      <DragOverlay>
        {activeItem && (
          <div className="bg-slate-800 border border-slate-600 rounded-lg p-2 shadow-xl opacity-90 pointer-events-none max-w-[150px]">
            {activeItem.type === 'availability_window' ? (
              <div className="flex items-center gap-1 text-xs text-emerald-400">
                <Clock size={12} />
                <span>
                  {formatTime12Hour(activeItem.start_time)}-{formatTime12Hour(activeItem.end_time)}
                </span>
              </div>
            ) : (
              <div className="text-xs">
                <div className="text-gray-400">
                  {formatTime12Hour(activeItem.start_time)}-{formatTime12Hour(activeItem.end_time)}
                </div>
                <div className="flex items-center gap-1" style={{ color: getUserColor(activeItem.caretaker) }}>
                  {activeItem.caretaker?.emoji && <span>{activeItem.caretaker.emoji}</span>}
                  <span>{activeItem.caretaker?.first_name || 'Unknown'}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
